import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { localizeDomain } from '../utils/localizeDomain';


const MarkdownRenderer = ({ content, context = "default" }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detectar el modo oscuro leyendo la clase "dark" en el <html>
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    checkDarkMode(); // estado inicial
    return () => observer.disconnect();
  }, []);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        code({ node, inline, className, children, ...props }) {
          const [copied, setCopied] = useState(false);
          const match = /language-(\w+)/.exec(className || '');

          const handleCopy = () => {
            navigator.clipboard.writeText(String(children).trim());
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          };

          if (!inline && match) {
            return (
              <div className="relative group">
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <SyntaxHighlighter
                  style={isDarkMode ? oneDark : prism}
                  language={match[1]}
                  PreTag="div"
                  customStyle={{ padding: '1rem', borderRadius: '0.5rem', margin: 0 }}
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              </div>
            );
          }

          return (
            <code className="bg-gray-200 dark:bg-gray-800 px-1 py-0.5 rounded" {...props}>
              {String(children).replace(/^`|`$/g, '')}
            </code>
          );
        },
        li({ children, ...props }) {
          let markerColor = "marker:text-gray-500 dark:marker:text-gray-300";
          let textSize = "text-base";
          let additionalClasses = "";

          if (context === "greenbox") {
            markerColor = "marker:text-green-500 dark:marker:text-green-300";
            textSize = "text-sm";
          } else if (context === "redbox") {
            markerColor = "marker:text-red-600 dark:marker:text-red-400";
            textSize = "text-sm";
          } else if (context === "content_list_no") {
            markerColor = "marker:text-gray-600 dark:marker:text-gray-400";
            textSize = "text-base";
            additionalClasses = "mb-2";
          } else if (context === "content_list") {
            markerColor = "marker:text-gray-600 dark:marker:text-gray-400";
            textSize = "text-base";
            additionalClasses = "mb-2";
          } else if (context === "box") {
            textSize = "text-sm";
          }

          return (
            <li className={`leading-relaxed ${textSize} ${markerColor} ${additionalClasses}`} {...props}>
              {children}
            </li>
          );
        },
        p({ children, ...props }) {
          if (context === "content_list_no") {
            return (
              <p className="my-1 text-left" {...props}>
                {children}
              </p>
            );
          }
          if (context === "content_list") {
            return (
              <p className="my-1 text-left" {...props}>
                {children}
              </p>
            );
          }
          return <p {...props}>{children}</p>;
        },
        ul({ children, ...props }) {
          if (context === "content_list_no") {
            return (
              <ul className="space-y-2 ml-0 pl-4 list-none" {...props}>
                {children}
              </ul>
            );
          }
          if (context === "content_list") {
            return (
              <ul className="space-y-2 ml-0 pl-4" {...props}>
                {children}
              </ul>
            );
          }
          return <ul {...props}>{children}</ul>;
        }
      }}
    >
      {localizeDomain(content)}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
